/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - REST API</h2>
    <p className={styles.description}>
      <b>Сервер</b> — компьютер со специальным программным обеспечением. Контролем за работой сервера занимается
      системный администратор.
    </p>
    <p className={styles.description}>
      <b>Дата-центр</b> - специально оборудованная площадка с круглосуточной технической поддержкой для размещенных
      серверов. Обеспечивает их бесперебойную работу.
    </p>
    <p className={styles.description}>
      <b>Бекенд</b> - программа, расположенная на сервере и способная обработать входящие HTTP-запросы и имеющая набор
      готовых действий на определенные запросы.
    </p>
    <p className={styles.description}>
      <b>API (интерфейс прикладного программирования)</b> — набор четко определенных правил связи между различными
      программными компонентами. Интерфейс описывает, что можно попросить программу сделать и что получится в
      результате.
    </p>
    <p className={styles.description}>
      <b>REST (representational state transfer)</b> — стиль бекенд-архитектуры, основанный на наборе принципов, которые
      описывают как сетевые ресурсы определяются и адресуются.
    </p>
    <p className={styles.description}>
      <b>REST API</b> — бекенд построенный по принципу REST. Служит прослойкой между веб-приложением и базой данных.
      Имеет стандартный интерфейс для обращения к ресурсам. Работает как веб-сайт, мы посылаем HTTP-запрос с клиента на
      сервер, а в ответ, вместо HTML-страницы, получаем данные в JSON-формате.
    </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <a
          className={styles.link}
          href="https://www.youtube.com/watch?time_continue=10&amp;v=llpr5924N7E"
          target="_blank"
          rel="noopener noreferrer"
        >
          Intro to REST (aka. What Is REST Anyway?)
        </a>
      </li>
      <li className={styles.list__item}>
        <a
          className={styles.link}
          href="https://www.sitepoint.com/developers-rest-api/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Do you know what a REST API is?
        </a>
      </li>
    </ul>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Формат запроса</h3>
      <p className={styles.description}>
        REST-сервис требует, чтобы клиент делал запрос на добавление, извлечение или изменение данных. Запрос обычно
        состоит из:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <b>HTTP-метод</b> — определяет какую операцию выполнять.
        </li>
        <li className={styles.list__item}>
          <b>Заголовок</b> — позволяет клиенту передавать информацию о запросе.
        </li>
        <li className={styles.list__item}>
          <b>Путь</b> — путь к ресурсу. Доступные пути описываются в документации бекенда.
        </li>
        <li className={styles.list__item}>
          <b>Тело</b> — дополнительный блок запроса, содержащий данные.
        </li>
      </ul>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.1 - HTTP-методы</h4>
        <p className={styles.description}>Выделяют 4 основных метода для работы с REST-сервисом. </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>POST</code> — создать новый ресурс.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>GET</code> — получить набор ресурсов или определенный ресурс по
            идентификатору.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>PUT</code> или <code className={styles.code}>PATCH</code> — обновление
            определенного ресурса по идентификатору.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>DELETE</code> — удаление определенного ресурса по идентификатору.
          </li>
        </ul>
        <a
          className={styles.link}
          href="https://developer.mozilla.org/ru/docs/Web/HTTP/Methods"
          target="_blank"
          rel="noopener noreferrer"
        >
          HTTP-методы на MDN
        </a>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.2 - Заголовки</h4>
        <p className={styles.description}>
          Заголовки содержат служебную информацию, относящуюся к пользователю или контенту запроса и как клиент
          собирается обрабатывать предоставленную ему информацию.
        </p>
        <p className={styles.description}>
          К примеру, тип контента, который клиент может обработать в ответе от сервера (заголовок{' '}
          <code className={styles.code}>Accept</code>) или который описывает тип ресурса, который клиент отправляет
          серверу или сервер отправляет клиенту (заголовок <code className={styles.code}>Content-Type</code>).{' '}
        </p>
        <p className={styles.description}>
          <b>MIME-типы</b> — варианты типов контента. Используются для указания содержимого запроса и ответа, состоят из
          типа и подтипа, которые разделены косой чертой <code className={styles.code}>/</code>.{' '}
        </p>
        <p className={styles.description}>
          Например текстовый файл, содержащий HTML, будет описан типом <code className={styles.code}>text/html</code>.
          Если файл содержит CSS, он будет описан как <code className={styles.code}>text/css</code>. Данные в формате
          JSON будут описаны как <code className={styles.code}>application/json</code>. Если клиент ожидает{' '}
          <code className={styles.code}>text/css</code>, а получает{' '}
          <code className={styles.code}>application/json</code>, он не сможет распознать и обработать контент.
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.3 - Пути</h4>
        <p className={styles.description}>
          Запросы должны содержать путь к ресурсу, над которым выполняется операция. Доступные пути (ендпоинты, ресурсы)
          описываются в документации бекенда.
        </p>
        <CopyBlock
          text={`https://bookstore.com/api/orders/289`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Такой путь явно указывает ресурс, даже если вы его никогда раньше не видели, потому что он является
          иерархическим и описательным. Мы обращаемся к заказу с идентификатором{' '}
          <code className={styles.code}>289</code>.
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.4 - Коды ответов</h4>
        <p className={styles.description}>
          На запрос клиента сервер отправляет ответ. Ответ содержит код состояния, чтобы информировать клиента о
          результате операции. Коды делятся на группы.
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>1XX</code> — несут информационное назначение
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>2XX</code> — коды успешного проведения операции
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>3XX</code> — описывают все, что связано с перенаправлением
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>4XX</code> — указывают на ошибки клиента
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>5XX</code> — указывают на ошибки на стороне сервера
          </li>
        </ul>
        <p className={styles.description}>
          Не нужно помнить каждый код состояния (их много), но необходимо знать наиболее распространенные и их значения.
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>200 (OK)</code> - стандартный ответ для успешных HTTP-запросов
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>201 (Created)</code> - стандартный ответ для HTTP-запроса, который привел к
            успешному созданию ресурса
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>400 (Bad Request)</code> - запрос не может быть обработан из-за неверного
            синтаксиса запроса или другой ошибки клиента.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>401 (Unauthorized)</code> - для доступа к ресурсу требуется авторизация.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>403 (Forbidden)</code> - у клиента нет разрешения на доступ к этому ресурсу.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>404 (Not Found)</code> - в настоящее время ресурс не найден. Возможно, он был
            удален или еще не существует.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>500 (Internal Server Error)</code> - общий ответ на непредвиденный сбой
            сервера, если нет более конкретной информации.
          </li>
        </ul>
        <a className={styles.link} href="https://httpstatuses.com/" target="_blank" rel="noopener noreferrer">
          Справочник HTTP-кодов
        </a>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Запрос-Ответ</h3>
      <p className={styles.description}>
        Предположим, у нас есть приложение, которое позволяет просматривать, создавать, редактировать и удалять клиентов
        и заказы небольшого книжного магазина, бекенд которого размещен на{' '}
        <code className={styles.code}>bookstore.com/api</code>. Используя полученные, знания мы можем, псевдокодом
        описать процесс запрос-ответ к бекенду.
      </p>
      <p className={styles.description}>Если бы мы хотели посмотреть всех клиентов, запрос будет выглядеть так. </p>
      <CopyBlock
        text={`GET bookstore.com/api/customers
Accept: application/json`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Ответ сервера. </p>
      <CopyBlock
        text={`Status: 200 OK
Content-Type: application/json
Body: JSON-данные о всех клиентах`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Для просмотра одного клиента мы указываем его идентификатор. </p>
      <CopyBlock
        text={`GET bookstore.com/api/customers/289
Accept: application/json`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Ответ сервера.</p>
      <CopyBlock
        text={`Status: 200 OK
Content-Type: application/json
Body: JSON-данные о клиенте`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Создание нового клиента.</p>
      <CopyBlock
        text={`POST bookstore.com/api/customers
Content-Type: application/json
Body: { "name": "Mango", "email": "mango@gmail.com" }`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Сервер добавляет уникальный идентификатор для этого объекта и возвращает его обратно клиенту.
      </p>
      <CopyBlock
        text={`Status: 201 Created
Content-type: application/json
Body: { "id": 18674, "name": "Mango", "email": "mango@gmail.com" }`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
